import React, { useMemo, useState } from "react";
import { withRouter } from "react-router-dom";
import Loader from "../../common/Loader";
import useFinanceurs from "./hooks/useFinanceurs";
import FinanceurRail from "./components/FinanceurRail";
import FinanceurPanel from "./components/FinanceurPanel";
import useAccessSettings from "../../../hooks/useInit/useAccessSettings";
import { userAccessEnum } from "../../../config/accessEnum";
import styles from "./FinanceurManager.module.scss";

/**
 * Page unifiée de gestion des financeurs (master-détail).
 *
 * Rail gauche : liste + capacité + création. Panneau droit : financeur
 * sélectionné (nom éditable, suppression) + ses enveloppes. La sélection est
 * portée par l'URL (`/financiers/:uuid`) pour le deep-link et le rafraîchissement.
 */
const FinanceurManager = (props) => {
  const {
    financiers,
    groupes,
    isLoading,
    loadFinanciers,
    refreshEnveloppes,
    creer,
    renommer,
    modifierContact,
    modifierAccordFinancement,
    supprimer,
  } = useFinanceurs();

  const [creating, setCreating] = useState(false);

  // La gestion des financeurs/enveloppes (CRUD) est réservée aux gestionnaires
  // (LS-637) — la sélection d'un financeur/enveloppe *sur une demande* reste
  // ouverte aux commerciaux, mais se fait depuis FinanceDetailV2, pas ici.
  // ⚠️ CHERCHER_FINANCIER est désormais accordé aux commerciaux (recherche pour
  // sélection sur une demande) : ne pas l'utiliser ici. VISUALISER_FINANCIER
  // reste strictement gestionnaire côté API (route financiers.show).
  const { accessActions } = useAccessSettings();
  const { granted: canManageFinanciers } = accessActions.canI({
    action: userAccessEnum.VISUALISER_FINANCIER,
  });

  const routeUuid = props.match.params.uuid || null;
  const selectedFinancier = useMemo(() => {
    if (creating) return null;
    return (
      financiers.find((f) => f.uuid === routeUuid) || financiers[0] || null
    );
  }, [creating, financiers, routeUuid]);

  const goTo = (uuid) =>
    props.history.push(uuid ? `/financiers/${uuid}` : "/financiers");

  const handleSelect = (uuid) => {
    setCreating(false);
    goTo(uuid);
  };

  const handleCreateStart = () => {
    setCreating(true);
    goTo(null);
  };

  const handleCreate = async (name) => {
    const uuid = await creer(name);
    if (!uuid) return;
    await loadFinanciers();
    setCreating(false);
    goTo(uuid);
  };

  const handleRename = async (uuid, name) => {
    const ok = await renommer(uuid, name);
    if (ok) await loadFinanciers();
    return ok;
  };

  const handleModifierContact = async (uuid, name, contact) => {
    const ok = await modifierContact(uuid, name, contact);
    if (ok) await loadFinanciers();
    return ok;
  };

  const handleModifierAccordFinancement = async (uuid, name, value) => {
    const ok = await modifierAccordFinancement(uuid, name, value);
    if (ok) await loadFinanciers();
    return ok;
  };

  const handleDelete = async (uuid) => {
    const ok = await supprimer(uuid);
    if (!ok) return;
    const list = await loadFinanciers();
    const next = (list || []).find((f) => f.uuid !== uuid);
    goTo(next ? next.uuid : null);
  };

  if (!canManageFinanciers) {
    return (
      <div className={styles.page}>
        <div className={styles.pageHead}>
          <span className={styles.pageIcon}>
            <i className="fa fa-university" />
          </span>
          <h1 className={styles.pageTitle}>Financeurs</h1>
        </div>
        <div className={styles.empty}>
          <i className="fa fa-lock" />
          <span>Accès réservé aux gestionnaires.</span>
        </div>
      </div>
    );
  }

  if (isLoading && financiers.length === 0) {
    return (
      <div className={styles.page}>
        <div className={styles.loaderWrap}>
          <Loader />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.pageHead}>
        <span className={styles.pageIcon}>
          <i className="fa fa-university" />
        </span>
        <h1 className={styles.pageTitle}>Financeurs</h1>
      </div>

      {financiers.length === 0 && !creating ? (
        <div className={styles.empty}>
          <i className="fa fa-university" />
          <span>Aucun financeur pour le moment.</span>
          <button
            type="button"
            className={styles.emptyBtn}
            onClick={handleCreateStart}
          >
            <i className="fa fa-plus" /> Créer un financeur
          </button>
        </div>
      ) : (
        <div className={styles.layout}>
          <FinanceurRail
            financiers={financiers}
            groupes={groupes}
            selectedUuid={creating ? null : selectedFinancier?.uuid || null}
            onSelect={handleSelect}
            onCreateStart={handleCreateStart}
          />
          <FinanceurPanel
            financier={selectedFinancier}
            creating={creating}
            onCreate={handleCreate}
            onRename={handleRename}
            onModifierContact={handleModifierContact}
            onModifierAccordFinancement={handleModifierAccordFinancement}
            onDelete={handleDelete}
            onEnveloppesChanged={refreshEnveloppes}
          />
        </div>
      )}
    </div>
  );
};

export default withRouter(FinanceurManager);
